import { useMemo, useState } from 'react';
import { BigNum, BN, QUOTE_PRECISION_EXP } from '@velocity-exchange/sdk';
import { baseSizeDisplayForPerpMarket } from 'src/utils/position';
import { OpenPosition } from '@velocity-exchange/common';

interface Params {
	position: OpenPosition | undefined;
	openPositionSizeBigNum: BigNum;
	baseSizeInputValueBigNum: BigNum;
	markPriceToUse: BigNum;
	autoCalculatedQuoteSize: BN;
	showAccountValues: boolean;
}

export default function useClosePositionModalState({
	position,
	openPositionSizeBigNum,
	baseSizeInputValueBigNum,
	markPriceToUse,
	autoCalculatedQuoteSize,
	showAccountValues,
}: Params) {
	const [quoteSizeInputValue, setQuoteSizeInputValue] = useState(() =>
		markPriceToUse.scalarMul(openPositionSizeBigNum.abs()).toFixed(2)
	);
	const [isQuoteSizeFromQuoteInput, setIsQuoteSizeFromQuoteInput] =
		useState(false);

	// When the base size input is being updated, either directly or via the position slider,
	// the quote size value gets updated "automatically" via the `useClosePositionUtils` hook.
	// If the user manually updates the quote size input though, we need to defer to their value.
	// This distinction is due to `TpSlOrderFormPopup` not having an editable quote size input.
	const quoteSizeInputValueToUse = isQuoteSizeFromQuoteInput
		? quoteSizeInputValue
		: BigNum.from(autoCalculatedQuoteSize, QUOTE_PRECISION_EXP).toFixed(2);

	const openPositionSizeDisplay = baseSizeDisplayForPerpMarket(
		openPositionSizeBigNum,
		position?.marketSymbol ?? '',
		!showAccountValues
	);

	const positionSliderValue = useMemo(() => {
		if (openPositionSizeBigNum.eqZero()) {
			return 0;
		}

		return (
			(baseSizeInputValueBigNum.toNum() / openPositionSizeBigNum.toNum()) * 100
		);
	}, [baseSizeInputValueBigNum, openPositionSizeBigNum]);

	return {
		setQuoteSizeInputValue,
		setIsQuoteSizeFromQuoteInput,
		quoteSizeInputValueToUse,
		openPositionSizeDisplay,
		positionSliderValue,
	};
}
